import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setLocationPermissionModal } from "@/core/redux/commonSlice";
import { requestLocationPermission } from "@/utils/locationPermission";

// Mounted once at the app root. Shown whenever the axios interceptor blocks a
// location-dependent request because the browser has no usable geolocation
// permission - see LOCATION_REQUIRED_ENDPOINTS in services/interceptor.tsx.
const EnableLocationModal: React.FC = () => {
  const dispatch = useDispatch();
  const show = useSelector((state: any) => state?.common?.locationPermissionModal);
  const [retrying, setRetrying] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);

  if (!show) return null;

  const handleClose = () => {
    dispatch(setLocationPermissionModal(false));
    setRetryFailed(false);
  };

  const handleTryAgain = async () => {
    setRetrying(true);
    setRetryFailed(false);
    try {
      await requestLocationPermission();
      dispatch(setLocationPermissionModal(false));
    } catch {
      setRetryFailed(true);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <>
      {/* Explicit z-index so this always sits above any other in-app modal
          (e.g. Add/Update Event) regardless of DOM mount order - Bootstrap's
          default .modal/.modal-backdrop z-index ties with theirs otherwise. */}
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 2050 }}
        onClick={handleClose}
      ></div>
      <div className="modal fade show d-block" style={{ zIndex: 2060 }} tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center p-4">
              <i className="ti ti-map-pin-off" style={{ fontSize: "40px", color: "#e14343" }}></i>
              <h5 className="mt-3 mb-2">Location Access Needed</h5>
              <p className="text-muted mb-3">
                This action needs your location to work correctly. Please enable location access for
                this site and try again.
              </p>
              <div className="text-start mb-3" style={{ fontSize: "14px" }}>
                <strong>How to enable it:</strong>
                <ol className="ps-3 mt-2 mb-0">
                  <li>Click the lock/info icon next to the site address in your browser.</li>
                  <li>Set &quot;Location&quot; permission to &quot;Allow&quot;.</li>
                  <li>Reload the page and click &quot;Try Again&quot; below.</li>
                </ol>
              </div>
              {retryFailed && (
                <p className="text-danger mb-3" style={{ fontSize: "13px" }}>
                  Still couldn&apos;t access your location. Please check the browser setting above.
                </p>
              )}
              <div className="d-flex justify-content-center gap-2">
                <button type="button" className="btn btn-outline-secondary" onClick={handleClose}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleTryAgain}
                  disabled={retrying}
                >
                  {retrying ? "Checking..." : "Try Again"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EnableLocationModal;
